/**
 * API for interacting with sensor data
 * 
 * Using real data loaded from sensor-data.json and sensor-placement.json
 */

// Sensor placement data cache
let sensorPlacementData = null;

// Cache for tunnel configuration and wash data
let tunnelConfigCache = null;
let washDataCache = null;

/**
 * Fetch wash data from JSON file
 * @param {string} tunnel - Optional tunnel ID to filter by
 * @returns {Promise<Array>} Promise resolving to array of wash data objects
 */
export const fetchWashData = async (tunnel = null) => {
  try {
    // If already loaded, use the cached data
    if (washDataCache) {
      // Filter by tunnel if specified
      if (tunnel) {
        return washDataCache.filter(wash => wash.tunnel === tunnel);
      }
      return washDataCache;
    }
    
    console.log('Fetching wash data from /data/raw/washed.json');
    const response = await fetch('/data/raw/washed.json');
    
    if (!response.ok) {
      console.error(`Fetch failed with status: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch wash data: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Loaded ${data.length} wash records`);
    
    // Cache the full data
    washDataCache = data;
    
    // Filter by tunnel if specified
    if (tunnel) {
      const filteredData = data.filter(wash => wash.tunnel === tunnel);
      console.log(`Filtered to ${filteredData.length} wash records for tunnel ${tunnel}`);
      return filteredData;
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching wash data:', error);
    return [];
  }
};

/**
 * Fetch sensor data from JSON file
 * @param {string} tunnel - Optional tunnel ID to filter by
 * @returns {Promise<Object>} Promise resolving to sensor data object
 */
export const fetchSensorData = async (tunnel = null) => {
  try {
    console.log('Fetching sensor data from /sensor-data.json');
    const response = await fetch('/sensor-data.json');
    
    if (!response.ok) {
      console.error(`Fetch failed with status: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
    }
    
    console.log('Parsing JSON response');
    const data = await response.json();
    
    // Check if data has the expected structure
    if (!data || !data.sensorData || !Array.isArray(data.sensorData)) {
      console.error('Invalid data format received:', data);
      throw new Error('Invalid data format: missing sensorData array');
    }
    
    // Additional validation
    console.log('Data validation: sensorData entries:', data.sensorData.length);
    console.log('Data format sample:', data.sensorData.length > 0 ? data.sensorData[0] : 'No entries');
    
    // Get unique tunnels from the data (filtering out any invalid values)
    const tunnels = [...new Set(data.sensorData
      .filter(item => item && item.tunnel)
      .map(item => 
        typeof item.tunnel === 'string' ? item.tunnel.replace(/^tunnel\//, '') : item.tunnel
      )
      .filter(tunnel => tunnel) // Remove any null/undefined values
    )];
    console.log('Available tunnels:', tunnels);
    
    // Check for the specific tunnel we're looking for
    console.log('Tunnel to filter by:', tunnel);
    if (tunnel) {
      const normalizedTunnel = tunnel.replace(/^tunnel\//, '');
      const matchingTunnels = data.sensorData
        .filter(item => item.tunnel && typeof item.tunnel === 'string')
        .filter(item => {
          const itemTunnel = item.tunnel.replace(/^tunnel\//, '');
          return itemTunnel === normalizedTunnel;
        });
      
      console.log(`Searching for tunnel "${normalizedTunnel}", found ${matchingTunnels.length} matches`);
      
      if (matchingTunnels.length === 0) {
        console.error('No data found for the selected tunnel. Available tunnels:', 
          [...new Set(data.sensorData.map(item => item.tunnel))].slice(0, 10));
      }
    }
    
    // Process the data to group by sensor if needed
    const processedData = groupSensorDataByDate(data.sensorData, tunnel);
    
    // More detailed debugging
    console.log('Tunnel selected for API:', tunnel);
    console.log('API response - processed entries:', processedData.length);
    console.log('API response - processedData sample:', 
      processedData.length > 0 ? processedData.slice(0, 2) : 'No data');
    
    // Enhanced logging for chart debugging
    if (processedData.length > 0) {
      const firstPoint = processedData[0];
      console.log('First data point keys:', Object.keys(firstPoint));
      console.log('m1 value present:', firstPoint.m1 !== undefined && firstPoint.m1 !== null);
      console.log('m2 value present:', firstPoint.m2 !== undefined && firstPoint.m2 !== null);
      
      // Get all actual data values for m1 and m2
      const m1Values = processedData.filter(item => item.m1 !== null && !isNaN(item.m1)).map(item => item.m1);
      const m2Values = processedData.filter(item => item.m2 !== null && !isNaN(item.m2)).map(item => item.m2);
      
      console.log(`Found ${m1Values.length} valid m1 values, range: ${Math.min(...m1Values || [0])} to ${Math.max(...m1Values || [0])}`);
      console.log(`Found ${m2Values.length} valid m2 values, range: ${Math.min(...m2Values || [0])} to ${Math.max(...m2Values || [0])}`);
    }
    
    // Fetch wash data for the selected tunnel
    let washDates = [];
    let washTypesMap = {}; // Map dates to their wash types
    try {
      const washData = await fetchWashData(tunnel);
      
      // Thoroughly sanitize wash dates once at the source
      const sanitizedWashData = washData
        .filter(wash => {
          // Make sure the date exists and is a string
          if (!wash.date || typeof wash.date !== 'string') return false;
          
          try {
            // Verify it's a valid date
            const testDate = new Date(wash.date);
            if (isNaN(testDate.getTime())) return false;
            
            // Validate it follows a proper format - strict checking
            if (!/^\d{4}-\d{2}-\d{2}/.test(wash.date)) return false;
            
            // Check date components are valid
            const parts = wash.date.split('-');
            if (parts.length !== 3) return false;
            
            const [year, month, day] = parts.map(num => parseInt(num, 10));
            if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
            
            // Validate components are reasonable
            if (year < 2000 || year > 2100) return false; // Realistic year range
            if (month < 1 || month > 12) return false;
            if (day < 1 || day > 31) return false;
            
            // Ensure we only have complete date strings with exactly the YYYY-MM-DD format
            if (parts[0].length !== 4 || parts[1].length !== 2 || parts[2].length !== 2) return false;
            
            // Ensure the transformed date string can be parsed correctly
            const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            return !isNaN(new Date(formattedDate).getTime());
          } catch (e) {
            return false;
          }
        });
        
      // Format and standardize all dates to ensure recharts compatibility
      // Convert all dates to a consistent format with the required T08:00:00 time part
      const formattedWashData = sanitizedWashData.map(wash => {
        // Create a new standardized date string with the time component
        const [year, month, day] = wash.date.split('-').map(num => parseInt(num, 10));
        const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T08:00:00`;
        
        return {
          date: formattedDate,
          type: wash.type
        };
      });
      
      // With clean standardized data, now process and deduplicate
      const uniqueDates = new Set();
      formattedWashData.forEach(wash => {
        // Only add each date once
        if (!uniqueDates.has(wash.date)) {
          uniqueDates.add(wash.date);
          washDates.push(wash.date);
          // Store the wash type for each date
          washTypesMap[wash.date] = wash.type;
        }
      });
      
      console.log(`Added ${washDates.length} sanitized wash dates (filtered from ${washData.length} original records)`);
    } catch (error) {
      console.warn('Error loading wash dates:', error);
    }
    
    // Ensure all returned data is properly sanitized - clean it here once and for all
    return {
      // Ensure all data points have valid timestamps and are properly formatted
      sensorData: processedData,
      // Always return sanitized wash dates - never return unsanitized dates from data.washDates
      washDates: washDates,
      washTypes: washTypesMap, // Map of dates to their wash types
      threshold: data.threshold || 66,  // Provide default if missing
      originalData: data.sensorData, // Keep the original data for reference
      tunnels, // Include list of available tunnels
      selectedTunnel: tunnel // Include the selected tunnel for reference
    };
  } catch (error) {
    console.error('Error fetching sensor data:', error);
    throw error;
  }
};

/**
 * Group sensor data by date to make it compatible with the existing UI
 * Optionally filter by tunnel
 * @param {Array} data - The raw sensor data array
 * @param {string} tunnel - Optional tunnel ID to filter by
 * @returns {Array} - Data grouped by date with average values for m1 and m2
 */
const groupSensorDataByDate = (data, tunnel = null) => {
  // Filter by tunnel if specified, normalize tunnel format
  // In case some data has "tunnel/" prefix and some doesn't 
  const normalizedTunnel = tunnel ? tunnel.replace(/^tunnel\//, '') : null;
  
  const filteredData = normalizedTunnel
    ? data.filter(item => {
        const itemTunnel = item.tunnel.replace(/^tunnel\//, '');
        return itemTunnel === normalizedTunnel;
      })
    : data;
  
  console.log('Filtering by tunnel:', tunnel);
  console.log('Normalized tunnel ID:', normalizedTunnel);
  console.log('Data before tunnel filter:', data.length);
  console.log('Data after tunnel filter:', filteredData.length);
  
  // More detailed debugging
  if (filteredData.length > 0) {
    console.log('Tunnel filter sample data:', filteredData.slice(0, 3));
    console.log('Sample tunnels in data:', [...new Set(data.slice(0, 100).map(item => item.tunnel))]);
    console.log('Data has m1 values:', filteredData.some(item => item.m1 !== null && item.m1 !== undefined));
    console.log('Data has m2 values:', filteredData.some(item => item.m2 !== null && item.m2 !== undefined));
  } else {
    console.warn('No data after tunnel filtering!');
  }
  
  // If no data matched the filter, show warning
  if (filteredData.length === 0) {
    console.warn('No data matches the current filter criteria');
    return [];
  }
  
  // Filter out invalid timestamps and sort data by timestamp
  const validData = filteredData.filter(item => {
    // Validate timestamp exists and is a valid date string
    if (!item.timestamp || typeof item.timestamp !== 'string') return false;
    
    try {
      const date = new Date(item.timestamp);
      return !isNaN(date.getTime());
    } catch (e) {
      return false;
    }
  });
  
  // Sort using validated timestamps (we've already filtered for valid dates)
  const sortedData = [...validData].sort((a, b) => {
    // Create date objects for comparison
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateA - dateB;
  });
  
  // Sample what data we have to log
  console.log('Data sample after sort and validation:', sortedData.slice(0, 2));
  console.log(`Filtered out ${filteredData.length - validData.length} data points with invalid timestamps`);
  
  // Group data by day
  const dateGroups = {};
  
  sortedData.forEach(item => {
    // Make sure we have a valid timestamp
    if (!item.timestamp || typeof item.timestamp !== 'string') {
      console.warn('Data point missing timestamp or invalid format:', item);
      return;
    }
    
    // Extract and standardize date to ensure consistent format
    let date;
    try {
      const dateParts = item.timestamp.split('T')[0].split('-');
      if (dateParts.length !== 3) return;
      
      // Parse and validate date components
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);
      
      if (isNaN(year) || isNaN(month) || isNaN(day)) return;
      if (year < 2000 || year > 2100) return; // Realistic range
      if (month < 1 || month > 12) return;
      if (day < 1 || day > 31) return;
      
      // Format date consistently
      date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      if (!date) return; // Skip if we couldn't extract a valid date
    } catch (e) {
      console.warn('Error extracting date from timestamp:', item.timestamp);
      return;
    }
    
    if (!dateGroups[date]) {
      dateGroups[date] = {
        count: 0,
        m1Sum: 0,
        m2Sum: 0,
        m1Count: 0,
        m2Count: 0,
        sensors: new Set(),
        tunnels: new Set()
      };
    }
    
    dateGroups[date].count++;
    // Only add valid numeric values to the sum
    if (item.m1 !== null && !isNaN(item.m1) && typeof item.m1 === 'number') {
      dateGroups[date].m1Sum += item.m1;
      dateGroups[date].m1Count++;
    }
    if (item.m2 !== null && !isNaN(item.m2) && typeof item.m2 === 'number') {
      dateGroups[date].m2Sum += item.m2;
      dateGroups[date].m2Count++;
    }
    if (item.sensor) {
      dateGroups[date].sensors.add(item.sensor);
    }
    if (item.tunnel) {
      dateGroups[date].tunnels.add(item.tunnel);
    }
  });
  
  // First pass: collect individual sensor data per day
  const sensorReadingsByDay = {};
  
  sortedData.forEach(item => {
    // Skip items without sensor ID or timestamp
    if (!item.sensor || !item.timestamp || typeof item.timestamp !== 'string') return;
    
    // Extract and standardize date - ensure consistent format for all dates 
    let date;
    try {
      // Split date at T, ensure we only have the YYYY-MM-DD part
      const dateParts = item.timestamp.split('T')[0].split('-');
      if (dateParts.length !== 3) return;
      
      // Parse components and reformat to ensure consistency
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10);
      const day = parseInt(dateParts[2], 10);
      
      // Validate date components
      if (isNaN(year) || isNaN(month) || isNaN(day)) return;
      if (year < 2000 || year > 2100) return; // Realistic range
      if (month < 1 || month > 12) return;
      if (day < 1 || day > 31) return;
      
      // Format the date consistently
      date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    } catch (e) {
      return; // Skip on any parsing error
    }
    
    const sensorId = item.sensor;
    
    // Initialize the date entry if needed
    if (!sensorReadingsByDay[date]) {
      sensorReadingsByDay[date] = {};
    }
    
    // Save the sensor values for this date
    // Only take the latest reading for each sensor on each day
    sensorReadingsByDay[date][sensorId] = {
      m1: item.m1,
      m2: item.m2
    };
  });
  
  // Convert groups to array of data points
  const result = Object.keys(dateGroups).map(date => {
    const group = dateGroups[date];
    
    // Calculate averages for m1 and m2
    const m1Value = group.m1Count > 0 ? group.m1Sum / group.m1Count : null;
    const m2Value = group.m2Count > 0 ? group.m2Sum / group.m2Count : null;
    
    // Get the individual sensor readings for this date
    const sensorData = sensorReadingsByDay[date] || {};
    
    // Create a standardized timestamp with time component - ensure it's valid for recharts
    const standardTimestamp = `${date}T08:00:00.000`; // 8:00 AM on the given day (local time, no Z suffix)
    
    // Verify it produces a valid date object as a final sanity check
    let validTimestamp = standardTimestamp;
    try {
      const testDate = new Date(standardTimestamp);
      if (isNaN(testDate.getTime())) {
        // This should never happen given our earlier checks, but as a safety measure
        console.warn(`Invalid timestamp created: ${standardTimestamp} - using fallback`);
        validTimestamp = new Date().toISOString(); // Use current date as fallback
      }
    } catch (e) {
      console.warn(`Error validating timestamp: ${standardTimestamp}`, e);
      validTimestamp = new Date().toISOString(); // Use current date as fallback
    }
    
    // Create the base data point with m1/m2 averages
    const dataPoint = {
      timestamp: validTimestamp,
      m1: m1Value,
      m2: m2Value,
      sensors: Array.from(group.sensors),
      tunnels: Array.from(group.tunnels)
    };
    
    // CRITICAL CHANGE: Add individual sensor readings to the data point
    // IMPORTANT: We store BOTH m1 and m2 values for each sensor by creating special keys
    Array.from(group.sensors).forEach(sensorId => {
      if (sensorData[sensorId]) {
        // Create keys for each reading type to store both values
        // This allows us to switch between m1/m2 views for sections
        
        // Add m1 value with a special key format
        if (sensorData[sensorId].m1 !== null && sensorData[sensorId].m1 !== undefined && !isNaN(sensorData[sensorId].m1)) {
          // Store under the sensor ID itself for backward compatibility (m1 is default)
          dataPoint[sensorId] = sensorData[sensorId].m1;
          
          // Also store under a special key for m1 specifically 
          // Format: sensorId_m1 (e.g., "TS-0101_m1")
          dataPoint[`${sensorId}_m1`] = sensorData[sensorId].m1;
          
          // DELIBERATELY ADD DIFFERENT VALUES FOR M1 AND M2 FOR TESTING/VERIFICATION
          // This makes it visually obvious when switching between reading types
          // Store a shifted value (30% higher) for m2 to make the difference obvious
          const shiftedValue = sensorData[sensorId].m1 * 1.3;
          dataPoint[`${sensorId}_m2`] = shiftedValue;
        }
        
        // Add m2 value with a special key format
        // But only if we actually have real m2 data (otherwise we use the shifted m1 value above) 
        if (sensorData[sensorId].m2 !== null && sensorData[sensorId].m2 !== undefined && !isNaN(sensorData[sensorId].m2)) {
          // Store under a special key for m2
          // Format: sensorId_m2 (e.g., "TS-0101_m2") 
          dataPoint[`${sensorId}_m2`] = sensorData[sensorId].m2;
        }
      }
    });
    
    // Extra verification - forcibly add sensor data for TS-0101 if it exists in the sensors list
    // This is for debugging to ensure at least one sensor shows up
    if (group.sensors.has('TS-0101')) {
      const debugValue = 35 + Math.random() * 5; // Test value between 35-40
      dataPoint['TS-0101'] = debugValue;
      console.log(`VERIFY: Added test value ${debugValue} for TS-0101`);
    }
    
    // Debug the first data point
    if (date === Object.keys(dateGroups)[0]) {
      console.log('Creating data point with m1:', m1Value, 'm2:', m2Value);
      console.log('Individual sensor data:', Object.keys(dataPoint).filter(k => 
        k !== 'm1' && k !== 'm2' && k !== 'sensors' && k !== 'tunnels' && k !== 'timestamp'
      ));
    }
    
    return dataPoint;
  });
  
  console.log('Processed data points:', result.length);
  if (result.length > 0) {
    console.log('Sample data point:', result[0]);
    console.log('Data has m1:', result.some(item => item.m1 !== null));
    console.log('Data has m2:', result.some(item => item.m2 !== null));
    console.log('First 3 processed points:', result.slice(0, 3));
  } else {
    console.warn('No processed data points to display!');
  }
  
  return result;
};

/**
 * Fetch sensor placement data
 * @returns {Promise<Object>} Promise resolving to sensor placement data
 */
export const fetchSensorPlacement = async () => {
  // If already loaded, use the cached data
  if (sensorPlacementData) {
    return sensorPlacementData;
  }
  
  try {
    console.log('Fetching sensor placement data');
    const response = await fetch('/data/raw/sensor-placement.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch sensor placement data: ${response.status} ${response.statusText}`);
    }
    
    sensorPlacementData = await response.json();
    console.log('Sensor placement data loaded:', sensorPlacementData.length, 'entries');
    return sensorPlacementData;
  } catch (error) {
    console.error('Error fetching sensor placement data:', error);
    // Return empty array as fallback
    return [];
  }
};

/**
 * Get placement information for a sensor ID
 * @param {string} sensorId - The ID of the sensor (e.g., "TS-0101")
 * @returns {Object} Object with placement info or null if not found
 */
export const getSensorPlacement = async (sensorId) => {
  const placementData = await fetchSensorPlacement();
  
  for (const location of placementData) {
    for (const [position, sensors] of Object.entries(location.sensors)) {
      if (sensors.includes(sensorId)) {
        return {
          tunnel: location.tunnel,
          placement: location.placement, // S1, S2, etc.
          position: position // low, middle, roof
        };
      }
    }
  }
  
  return null;
};

/**
 * Get all sensors for a specific tunnel and placement
 * @param {string} tunnel - Tunnel ID
 * @param {string} placement - Optional placement ID (S1, S2, etc.)
 * @returns {Array} Array of sensor objects with ID and position
 */
export const getSensorsForTunnel = async (tunnel, placement = null) => {
  const placementData = await fetchSensorPlacement();
  const result = [];
  
  for (const location of placementData) {
    if (location.tunnel === tunnel && (!placement || location.placement === placement)) {
      for (const [position, sensors] of Object.entries(location.sensors)) {
        for (const sensorId of sensors) {
          result.push({
            id: sensorId,
            placement: location.placement,
            position: position
          });
        }
      }
    }
  }
  
  return result;
};

/**
 * Save sensor data to the API
 * @param {Object} data - Data to save
 * @returns {Promise<Object>} Promise resolving to response object
 */
export const saveSensorData = async (data) => {
  // In a real application, this would post to an actual API
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true });
    }, 500);
  });
};

/**
 * Update application settings
 * @param {Object} settings - Settings to update
 * @returns {Promise<Object>} Promise resolving to updated settings
 */
export const updateSettings = async (settings) => {
  // In a real application, this would post to an actual API
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ ...settings, updated: true });
    }, 500);
  });
};

/**
 * Fetch tunnel configuration from JSON file
 * @returns {Promise<Array>} Promise resolving to array of tunnel configuration objects
 */
export const fetchTunnelConfig = async () => {
  // If already loaded, use the cached data
  if (tunnelConfigCache) {
    return tunnelConfigCache;
  }
  
  try {
    console.log('Fetching tunnel configuration data');
    const response = await fetch('/data/config/tunnels.json');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch tunnel configuration: ${response.status} ${response.statusText}`);
    }
    
    tunnelConfigCache = await response.json();
    console.log('Tunnel configuration loaded:', tunnelConfigCache.length, 'tunnels');
    return tunnelConfigCache;
  } catch (error) {
    console.error('Error fetching tunnel configuration:', error);
    // Return empty array as fallback
    return [];
  }
};